import { z } from 'zod';
import { RowVersionSchema } from '../common/row-version.js';
// Response schemas for zones. Field-exposure rule (ANATOMY §L7): the list row
// carries what the list + its coverage cell render; the detail adds the
// (deferred) contents counts. There is no `display_order` — zones aren't
// reorderable — and no detail-only identity beyond name + default + status (the
// thin entity has no editable section; name + default live in the header).
// ── GET /api/projects/:projectId/zones — list row ───────────────────────────
// `assigned_people_count` (distinct people across the zone's zone-level chains)
// + `required_unfilled_count` (required zone-level responsibilities with no lead)
// feed the shipped `ResponsibilityCoverage` cell (people count + required-gap
// chip). Both are 0 when the field-force module is off — the column is hidden then.
export const ZoneListItemSchema = z
    .object({
    row_version: RowVersionSchema,
    id: z.number().int(),
    name: z.string().max(32),
    is_default: z.boolean(),
    is_disabled: z.boolean(),
    assigned_people_count: z.number().int(),
    required_unfilled_count: z.number().int(),
    // ADR-052 §5 proactive surfacing: may THIS viewer manage this zone's responsibility
    // chains? (users:manage + level-set ∋ zone + own-scope). Greys the manage affordance
    // where a dual-audience viewer sees a zone outside their own scope — surfaced, not
    // failed-on-click (fixes the prior zones gap).
    can_manage_responsibilities: z.boolean(),
})
    .strict();
export const ZonesListResponseSchema = z
    .object({ items: z.array(ZoneListItemSchema) })
    .strict();
// ── GET /api/projects/:projectId/zones/:zoneId — detail ─────────────────────
// The contents counts feed the read-only `StatStrip` — the deferred count
// drill-through seam: 0 until sites / warehouses / vehicle-pools land (the value
// slot already takes a node so a future link drops in without a wire change).
export const ZoneDetailSchema = z
    .object({
    row_version: RowVersionSchema,
    id: z.number().int(),
    name: z.string().max(32),
    is_default: z.boolean(),
    is_disabled: z.boolean(),
    site_count: z.number().int(),
    warehouse_count: z.number().int(),
    vehicle_pool_count: z.number().int(),
    // ADR-052 §5 proactive surfacing — same per-zone own-scope flag the list row carries,
    // so the peek's manage affordance greys (Tooltip) for a capable viewer outside their
    // own scope rather than failing on click. Computed by the SAME checker as the list.
    can_manage_responsibilities: z.boolean(),
})
    .strict();
// ── POST create — minimal id (FE refetches the list) ────────────────────────
export const CreatedZoneResponseSchema = z.object({ id: z.number().int() }).strict();
